//
// Pin store
//
// Stores and manages all pins that can be administered by the
// authenticated user.
//

import * as client from 'api/client';
import { pin as pinSchema } from 'api/schema';
import { normalize } from 'normalizr';
import { LOGOUT, getToken } from './auth';
import { RECEIVE_ENTITIES } from './user';
import { OPEN_LESSON } from './lessons';
import { showSuccess, showError } from './messages';

// ------------------------------------
// Pin categories
// ------------------------------------

export const PUBLIC_PINS = 'PUBLIC_PINS';
export const LESSON_PINS = 'LESSON_PINS';

// ------------------------------------
// Action definitions
// ------------------------------------

export const SHOW_PINS    = 'PINS/SHOW';
export const HIDE_PINS    = 'PINS/HIDE';
export const FILTER_PINS  = 'PINS/FILTER';
export const REQUEST_PINS = 'PINS/REQUEST';
export const RECEIVE_PINS = 'PINS/RECEIVE';
export const CREATE_PIN   = 'PINS/CREATE';
export const PIN_CREATED  = 'PINS/CREATE_OK';
export const UPDATE_PIN   = 'PINS/UPDATE';
export const PIN_UPDATED  = 'PINS/UPDATE_OK';
export const DELETE_PIN   = 'PINS/DELETE';
export const PIN_DELETED  = 'PINS/DELETE_OK';
export const ERROR        = 'PINS/ERROR';

// ------------------------------------
// Action creators
// ------------------------------------

export const showPins = category => ({
  type: SHOW_PINS,
  category
});

export const hidePins = category => ({
  type: HIDE_PINS,
  category
});

export const filterPins = school => ({
  type: FILTER_PINS,
  school
});

export const togglePins = category => (dispatch, getState) => {
  const { pins } = getState();

  pins.visible.includes(category)
    ? dispatch(hidePins(category))
    : dispatch(showPins(category));
};

export const fetchPins = () => dispatch => {
  dispatch({ type: REQUEST_PINS });

  return client.get('/api/public/pins')
    .then(json => {
      const { entities } = normalize(json, [ pinSchema ]);
      const { pins } = entities;
      dispatch({ type: RECEIVE_PINS, pins });
    })
    .catch(console.error);
};

export const createPin = data => dispatch => {
  dispatch({ type: CREATE_PIN });

  // Note: this is submitted as multipart data, not json.
  return client.submit('/api/public/pins', data)
    .then(pin => {
      dispatch(showSuccess('pin.create.success'));
      dispatch({ type: PIN_CREATED, pin });
    })
    .catch(error => {
      dispatch(showError('pin.create.failed'));
      dispatch(fail());
    });
};

export const updatePin = pin => (dispatch, getState) => {
  dispatch({ type: UPDATE_PIN });

  const state = getState();
  const token = getToken(state);

  return client.put(`/api/pins/${pin.id}`, pin, token)
    .then(pin => dispatch({ type: PIN_UPDATED, pin }))
    .catch(error => dispatch(fail(error)));
};

export const deletePin = id => (dispatch, getState) => {
  dispatch({ type: DELETE_PIN });

  const state = getState();
  const token = getToken(state);

  return client.del(`/api/pins/${id}`, token)
    .then(() => {
      dispatch(showSuccess('pin.delete.success'));
      dispatch({ type: PIN_DELETED, id });
    })
    .catch(error => {
      dispatch(showError('pin.delete.failed'));
      dispatch(fail());
    });
};

export const toggleVisibilty = id => (dispatch, getState) => {
  const state = getState();
  const pin = state.pins.items[id];

  dispatch(updatePin({
    ...pin,
    public: pin.public ? 0 : 1
  }));
};

export const fail = () => ({ type: ERROR });

// ------------------------------------
// Initial state
// ------------------------------------

const initialState = {
  items: {},
  visible: [],
  filter: null,
  pending: false
};

// ------------------------------------
// Reducer
// ------------------------------------

export default (state = initialState, action) => {
  switch (action.type) {
    case RECEIVE_ENTITIES: {
      const pins = action.entities.pins || {};
      const items = {
        ...state.items,
        ...pins
      };

      return { ...state, items };
    }

    case SHOW_PINS: {
      const visible = [ ...state.visible, action.category ];
      return { ...state, visible };
    }

    case HIDE_PINS: {
      const visible = state.visible.filter(category =>
        category !== action.category
      );

      return { ...state, visible };
    }

    case FILTER_PINS: {
      const filter = action.school;
      return { ...state, filter };
    }

    case REQUEST_PINS: {
      return { ...state, pending: true };
    }

    case RECEIVE_PINS: {
      const { pins } = action;
      const items = { ...state.items, ...pins }
      return { ...state, items, pending: false  };
    }

    case CREATE_PIN:
    case UPDATE_PIN:
    case DELETE_PIN: {
      return { ...state, pending: true };
    }

    case PIN_CREATED: {
      const { pin } = action;

      return {
        ...state,
        items: { ...state.items, [pin.id]: pin },
        pending: false
      };
    }

    case PIN_UPDATED: {
      const { pin } = action;

      return {
        ...state,
        items: { ...state.items, [pin.id]: pin },
        pending: false
      };
    }

    case PIN_DELETED: {
      const items = { ...state.items };
      delete items[action.id];

      return { ...state, items, pending: false };
    }

    case OPEN_LESSON: {
      const visible = [ LESSON_PINS ];
      return { ...state, visible };
    }

    case ERROR:
      return { ...state, pending: false };

    case LOGOUT:
      return initialState;

    default:
      return state
  }
};

// ------------------------------------
// Utils
// ------------------------------------

export const getAllPins = state => {
  return Object.values(state.pins.items);
};

export const getPins = (state, ids) => {
  const { items } = state.pins;
  return ids.map(id => ({ ...items[id] }));
};

export const getPin = (state, id) => {
  const { items } = state.pins;
  return { ...items[id] };
};

export const getVisiblePins = state => {
  if (!!state.input.code) {
    return [];
  }

  const { visible, filter } = state.pins;
  const items = Object.values(state.pins.items);

  let publicPins = [];
  let lessonPins = [];

  if (visible.includes(PUBLIC_PINS)) {
    publicPins = filter
      ? items.filter(pin => pin.public && pin.school === filter)
      : items.filter(pin => pin.public);
  }

  if (visible.includes(LESSON_PINS)) {
    const lessonId = state.lessons.selected;
    lessonPins = items.filter(pin => pin.lesson_id === lessonId);
  }

  return [ ...publicPins, ...lessonPins ];
};



// WEBPACK FOOTER //
// ./src/store/modules/pins.js